<script>
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import ChartEnergyEfficiency from '$lib/PageSections/Charts/ChartEnergyEfficiency/ChartEnergyEfficiency.svelte';
	import CustomerStory from '$lib/PageSections/CustomerStory/CustomerStory.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';
	import ProductRecommendation from '$lib/PageSections/Products/ProductRecommendation.svelte';
	import Reviews from '$lib/PageSections/Reviews/Reviews.svelte';
	import TrustedChoice from '$lib/PageSections/TrustedChoice/TrustedChoice.svelte';

	import { ThermalProduct } from '$lib/data/products/products-data.js';
	import { frankLloydWrightCustomerStory } from '$routes/(public)/resources/case-studies/customer-stories.js';

	import paintingWindowImage from './images/paintingWindow.jpg';
	import windowWithBowlImage from './images/windowWithBowl.jpg';
</script>

<Section>
	<h2 class="text-left">The Ups &amp; Downs Of Historic Windows</h2>
	<p class="text-left">
		Many of America's historic windows were made from trees 200-300 years old at
		the time they were logged. The wood is dense and rot resistant. You can't
		find it anymore. And a lot of the glass was handblown before modern,
		machine-made optically-perfect float glass became widely available. They
		simply weren't made to meet the modern standards of comfort and quiet we
		expect in today's world.
	</p>
	<Image src={windowWithBowlImage} loading="eager" />
	<p class="text-left">
		Owners of older homes often face a difficult choice: do I keep my original
		windows and suffer through the cold winters, hot summers, and high energy
		bills? Or do I replace them with new, energy efficient windows and lose all
		the charm and aesthetics I love?
	</p>
	<p class="text-left">
		Some old home lovers choose restoration - a process that upgrades the
		historic windows without losing their visual appeal, but restoration can be
		very expensive, very disruptive, and specialized window restorers are often
		in high demand.
	</p>
	<Image src={paintingWindowImage} />
	<p class="text-center">
		Fortunately, there is an easier and more affordable option to upgrade your
		historic windows without losing any of their charm.
	</p>

	<h2 class="text-left">The Best Of Both Worlds</h2>
	<p class="text-left">
		With our patented Snug Fit compression tubing and premium acrylic glazing,
		our Thermal Window Inserts are placed inside your existing window frames to
		reduce the amount of cold drafts and heat loss happening through your
		windows. This reduces the load on your HVAC and leads to lower energy use -
		saving homeowners an average of 20% per year on energy expenses!
	</p>
	<p class="text-left">
		But the best part? They are non-invasive - requiring no modifications to
		your existing window frames and seamlessly blending in. In fact, if it
		wasn't for how much warmer they make your home - you'd forget they were
		there!
	</p>
</Section>

<CustomerStory story={frankLloydWrightCustomerStory} />

<Section>
	<h2 class="text-left">More Quiet, More Comfort, More Savings</h2>
	<p class="text-left">
		In addition to a substantial reduction in energy costs, Indow Thermal
		Inserts also provide significant <a href="/solutions/noise-performance"
			>noise reduction</a
		>, keeping your home up to 50% quieter. It's a win-win-win for your pocket
		book, the planet, and your day-to-day comfort.
	</p>
	<p class="text-left">
		Our air-tight seal not only keeps noise out and your ideal temperature in,
		but it also <a href="/solutions/prevent-window-condensation"
			>reduces condensation</a
		>
		and
		<a href="/solutions/block-pollen-and-pollutants"
			>blocks dust, pollen, and other airborne particles</a
		>. But that doesn't mean you'll have to live in a hermetically sealed home!
		With a gentle tug on the provided D-ring, you can break the seal and remove
		the inserts whenever you want to open your windows and let in some fresh
		air.
	</p>
</Section>

<ProductRecommendation product={ThermalProduct} />

<Section backgroundColor="grey-light">
	<h3 class="text-indow-grey-dark">Indow vs Replacement Windows</h3>
	<p>
		Adding Indow inserts to an existing window can make your windows perform as
		well (or better!) than a replacement window, but at a fraction of the cost
		and without the hassle and disruption of installing new windows.
	</p>
	<ChartEnergyEfficiency />
</Section>

<TrustedChoice />
<Reviews />
<GetAFreeEstimate />
